import { Injectable } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { FireSubService } from 'src/app/core/firebase/fireSub.service';
import { GameThemeSettingsDispatcher } from 'src/app/core/state/gameThemeSettings/gameThemeSettings.dispatcher';
import { GameThemeSettingsState } from 'src/app/core/state/gameThemeSettings/gameThemeSettings.state';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { SubscriptionHandlerService } from 'src/app/shared/services/subscription-handler/subscription-handler.service';
import { AvatarsComponent } from './component/the-rail/profile-game-settings/avatars/avatars.component';
import { CardsComponent } from './component/the-rail/profile-game-settings/cards/cards.component';
import { BackgroundComponent } from './component/the-rail/profile-game-settings/background/background.component';
import { FeltComponent } from './component/the-rail/profile-game-settings/felt/felt.component';
import { BetButtonsComponent } from './component/the-rail/profile-game-settings/bet-buttons/bet-buttons.component';
import { MyWalletComponent } from './component/the-rail/profile-game-settings/my-wallet/my-wallet.component';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { CryptoStateDispatcher } from 'src/app/core/state/crypto/crypto.dispatcher';
import { AuthStoreDispatcher } from 'src/app/core/state/auth/auth.dispatcher';
import { MoreInformationComponet } from './component/new-profile/more-information/more-information.component';
import { ProfileData } from 'src/app/core/state/profile/profile.state';
import { SopoDialogSettings } from 'src/app/sopo-popup/interfaces/dialog';

export type ActivePopover = {
        cards: boolean,
        backgrounds: boolean,
        felts: boolean,
        avatars: boolean,
        bets: boolean,
        nfts: boolean,
        more: boolean,
}

@Injectable({
    providedIn: 'root'
})
export class ProfilePageService {
    activePopover: ActivePopover = {
        cards: false,
        backgrounds: false,
        felts: false,
        avatars: false,
        bets: false,
        nfts: false,
        more: false,
    };
    activePopoverSubject = new BehaviorSubject<{[name: string]: boolean}>(this.activePopover);

    constructor(
        private subscriptionHandler: SubscriptionHandlerService,
        private fireSubService: FireSubService,
        private profileStateDispatcher: ProfileStateDispatcher,
        private gameThemeSettings: GameThemeSettingsDispatcher,
        private popoverController: PopoverController,
        private sopoPopupService: SopoPopupService,
        private cryptoDispatcher: CryptoStateDispatcher,
        private authStoreDispatcher: AuthStoreDispatcher,
    ) { }

    public async getLoginAndCreationTimes(): Promise<ILoginAndCreationTimes> {
        return await firstValueFrom(this.authStoreDispatcher.getUserData().pipe(
            map(userData => {
                if (userData) return {
                    lastSignInTime: userData.lastSignInTime,
                    creationTime: userData.creationTime,
                }
                else return {
                    lastSignInTime: 0,
                    creationTime: 0,
                };
            })
        ));
    }

    initSopoPlusObservable(): Observable<boolean> {
        return this.subscriptionHandler.mySubscription$.pipe(
            takeUntil(this.fireSubService.unsubscriber),
            map(subscribed => {
                if (subscribed && subscribed.isSubScribed) return true;
                return false;
            }),
            distinctUntilChanged()
        );
    }

    initIsUsingNftObservable(): Observable<boolean> {
        return this.profileStateDispatcher.getProfileState().pipe(
            map(profileState => {
                if(profileState && profileState.profileData) {
                    return profileState.profileData.useNftAsAvatar;
                }
                return false;
            }),
            distinctUntilChanged()
        );
    }

    initNftUrlObservable(): Observable<{image: string, cropped: boolean}> {
        return this.profileStateDispatcher.getProfileState().pipe(
            map(profileState => {
                if(profileState && profileState.profileData && profileState.profileData.nft) {
                    const nft = profileState.profileData.nft;
                    if (nft.usingCroppedImage) {
                        if (nft.croppedImageUrl) return {image: nft.croppedImageUrl, cropped: true};
                    return {image: 'assets/img/cropped_nft_loader.gif', cropped: true};
                    }
                    return {image: nft.imageUrl, cropped: false};
                }
                return {image: '', cropped: false};
            }),
            distinctUntilChanged()
        );
    }

    initGameThemeSettingsObservable(): Observable<GameThemeSettingsState> {
        return this.gameThemeSettings.getGameThemeSettingsState()
            .pipe(
                map(profileData => profileData),
            );
    }

    async showAvatars(event: Event) {
        if (this.activePopover.avatars) return;
        this.activePopover.avatars = true;
        this.activePopoverSubject.next({avatars: true});
        const popover = await this.popoverController.create({
            component: AvatarsComponent,
            cssClass: 'custom-profile-popover-avatars',
            event,
            translucent: true
        });
        popover.onWillDismiss().then(() => {
            this.activePopover.avatars = false;
            this.activePopoverSubject.next({avatars: false});
        });
        await popover.present();
    }

    async showCards(ev: any) {
        if (this.activePopover.cards) return;
        this.activePopover.cards = true;
        this.activePopoverSubject.next({cards: true});
        const popover = await this.popoverController.create({
            component: CardsComponent,
            cssClass: 'custom-profile-popover',
            event: ev,
            translucent: true,
        });
        popover.onWillDismiss().then(() => {
            this.activePopover.cards = false;
            this.activePopoverSubject.next({cards: false});
        });
        return await popover.present();
    }

    async showBackgrounds(ev: any) {
        if (this.activePopover.backgrounds) return;
        this.activePopover.backgrounds = true;
        this.activePopoverSubject.next({backgrounds: true});
        const popover = await this.popoverController.create({
            component: BackgroundComponent,
            cssClass: 'custom-profile-popover',
            event: ev,
            translucent: true,
        });
        popover.onWillDismiss().then(() => {
            this.activePopover.backgrounds = false;
            this.activePopoverSubject.next({backgrounds: false});
        });
        return await popover.present();
    }

    async showFelts(ev: any) {
        if (this.activePopover.felts) return;
        this.activePopover.felts = true;
        this.activePopoverSubject.next({felts: true});
        const popover = await this.popoverController.create({
            component: FeltComponent,
            cssClass: 'custom-profile-popover',
            event: ev,
            translucent: true,
        });
        popover.onWillDismiss().then(() => {
            this.activePopover.felts = false;
            this.activePopoverSubject.next({felts: false});
        });
        return await popover.present();
    }

    async showBets(ev: any) {
        if (this.activePopover.bets) return;
        this.activePopover.bets = true;
        this.activePopoverSubject.next({bets: true});
        const popover = await this.popoverController.create({
            component: BetButtonsComponent,
            cssClass: 'custom-profile-popover',
            event: ev,
            translucent: true,
        });
        popover.onWillDismiss().then(() => {
            this.activePopover.bets = false;
            this.activePopoverSubject.next({bets: false});
        });
        return await popover.present();
    }

    async showNfts(isInProfilePage: boolean) {
        if (this.activePopover.nfts) return;
        this.activePopover.nfts = true;
        this.activePopoverSubject.next({nfts: true});
        const isMetamaskLoggedIn = await firstValueFrom(this.cryptoDispatcher.isLoggedIn());
        if (!isMetamaskLoggedIn) {
            this.activePopover.nfts = false;
            this.activePopoverSubject.next({nfts: false});
            return;
        }

        const dialogSettings: SopoDialogSettings = {
            closeButton: true,
            data: {
                isInProfilePage,
            },
            sCss: ['sopo-popup-bg-new-background'],
            height: '80%',
        };
        if (!isInProfilePage) dialogSettings.zIndex = 2002;

        const dialogRef = this.sopoPopupService.openDialog('app', MyWalletComponent, dialogSettings)
        dialogRef.afterClosed.subscribe(() => {
            this.activePopover.nfts = false;
            this.activePopoverSubject.next({nfts: false});
        });
    }

    async showMoreInformation(profileData: ProfileData) {
        const loginAndCreationTimes = await this.getLoginAndCreationTimes();
        this.sopoPopupService.openDialog('app', MoreInformationComponet, {
            closeButton: true,
            data: {
                profileData,
                loginAndCreationTimes,
            }
        });
    }
}

export interface ILoginAndCreationTimes {
    lastSignInTime: number;
    creationTime: number;
}
